import { clsx } from "clsx";
import {
  ControllerFieldState,
  FieldPath,
  FieldValues,
  get,
  UseFormStateReturn,
} from "react-hook-form";

function flatInputFormState<TFieldValues extends FieldValues>(
  formState: Pick<UseFormStateReturn<TFieldValues>, "errors" | "touchedFields">,
  key: FieldPath<TFieldValues>,
) {
  const isError = get(formState.errors, key)?.message,
    isTouched = get(formState.touchedFields, key);
  return { isError, isTouched };
}
function inputClassNames<TFieldValues extends FieldValues>(
  formState: Pick<UseFormStateReturn<TFieldValues>, "errors" | "touchedFields">,
  key: FieldPath<TFieldValues>,
) {
  const { isError, isTouched } = flatInputFormState(formState, key);
  return clsx({ "ion-invalid": isError, "ion-touched": isTouched });
}
function inputClassNamesRaw<TFieldValues extends FieldValues>(
  formState: Pick<UseFormStateReturn<TFieldValues>, "errors" | "touchedFields">,
  key: FieldPath<TFieldValues>,
) {
  const { isError, isTouched } = flatInputFormState(formState, key);
  return { "ion-invalid": isError, "ion-touched": isTouched };
}

/**
 * The register() helpers above stay quiet until the field is touched. Inputs
 * rendered through `Controller` need the same gate: a form that validates on
 * mount would otherwise report every required field before anyone fills it in.
 */
function fieldStateErrorProps(
  fieldState: Pick<ControllerFieldState, "error" | "isTouched">,
) {
  const message = fieldState.isTouched ? (fieldState.error?.message ?? "") : "";
  return {
    className: clsx({
      "ion-invalid": Boolean(message),
      "ion-touched": fieldState.isTouched,
    }),
    errorText: message,
  };
}

/**
 * The same gate for fields wired with `register()`. A submit attempt counts as
 * touching everything, since it asks for every answer at once.
 */
function registeredErrorProps<TFieldValues extends FieldValues>(
  formState: Pick<
    UseFormStateReturn<TFieldValues>,
    "errors" | "isSubmitted" | "touchedFields"
  >,
  key: FieldPath<TFieldValues>,
) {
  const { isError, isTouched } = flatInputFormState(formState, key),
    reveal = Boolean(isTouched) || formState.isSubmitted,
    message = reveal && typeof isError === "string" ? isError : "";

  return {
    className: { "ion-invalid": Boolean(message), "ion-touched": reveal },
    errorText: message,
  };
}

export {
  fieldStateErrorProps,
  flatInputFormState,
  inputClassNames,
  inputClassNamesRaw,
  registeredErrorProps,
};
